import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";

import { componentTypeLabel, IPayerGroup, recSubtitle } from "../utils";

interface IProps {
  /** One entry per payer the contract covers (contract-wide recs are not segments and aren't passed). */
  segments: IPayerGroup[];
  /** Payer keys currently kept (e.g. "p4", "p20"). */
  selected: string[];
  busy: boolean;
  onToggle: (key: string) => void;
  onApply: () => void;
}

/**
 * "Segment first" — Ani's principle from her Grok modeling sessions: line of business (commercial vs
 * Medicare Advantage vs …) is decided BEFORE anything else, and one document is never allowed to stand in
 * for two lines of business. Each line of business is its own payer, and a ContractFeeSchedule is per
 * payer, so they are genuinely separate rate sets.
 *
 * Unticking a line of business REJECTS every recommendation under it — its contract term, base rate file,
 * rate calc, carveouts, rulesets. That is the same mechanism the Service Lines picker uses (a rejected rec
 * simply drops off the checklist); nothing is deleted, and re-ticking restores them to `pending`.
 */
const lobHint = (payerName: string | null): string => {
  const n = (payerName ?? "").toLowerCase();
  if (n.includes("medicare advantage")) return "Medicare Advantage";
  if (n.includes("first health") || n.includes("nab")) return "Rented network";
  return "Commercial";
};

function LineOfBusinessPicker({
  segments,
  selected,
  busy,
  onToggle,
  onApply,
}: IProps) {
  if (segments.length === 0) {
    return (
      <p className="text-700">
        This contract covers a single payer, so there is nothing to segment.
      </p>
    );
  }

  const skipped = segments.filter((s) => !selected.includes(s.key));

  return (
    <div>
      <div className="surface-50 border-1 surface-border border-round p-3 mb-3 text-sm line-height-3">
        <p className="mt-0 mb-2 text-700">
          A single contract document often prices more than one{" "}
          <strong>line of business</strong> &mdash; a commercial line and a
          Government Programs / Medicare Advantage line, say. Each is its own
          payer with its own contract fee schedule (a fee schedule is per
          payer), so they&rsquo;re modeled separately.
        </p>
        <p className="mt-0 mb-0 text-700">
          Untick a line of business to skip it &mdash; every recommendation
          under it (contract term, base rate file, rate calc, rules) is
          rejected, so nobody is ever told to build it and it drops out of the
          rest of the review. You can tick it back on at any time.
        </p>
      </div>

      <p className="text-700 mt-0">
        This contract covers {segments.length}{" "}
        {segments.length === 1 ? "line of business" : "lines of business"}.
      </p>

      <div className="grid">
        {segments.map((seg) => {
          const isSelected = selected.includes(seg.key);
          const rateCalc = seg.recs.find(
            (r) => r.componentType === "rate_calculation"
          );
          const basis = rateCalc ? recSubtitle(rateCalc) : null;
          const types = Array.from(
            new Set(seg.recs.map((r) => componentTypeLabel(r.componentType)))
          );
          return (
            <div className="col-12 md:col-6" key={seg.key}>
              {/* One click target for the whole card; the checkbox is presentational (see
                  ServiceLinePicker for why nesting an interactive checkbox in a button breaks toggling). */}
              <div
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={busy ? -1 : 0}
                onClick={() => !busy && onToggle(seg.key)}
                onKeyDown={(e) => {
                  if (busy) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(seg.key);
                  }
                }}
                className={`w-full surface-card border-round p-3 border-1 ${
                  busy ? "" : "cursor-pointer"
                } ${
                  isSelected
                    ? "border-primary"
                    : "border-transparent surface-border"
                }`}
              >
                <div className="flex align-items-center gap-2 flex-wrap">
                  <Checkbox checked={isSelected} readOnly disabled={busy} />
                  <span className="font-medium">
                    {seg.payerName ?? `Payer ${seg.payerId}`}
                  </span>
                  <Tag
                    className="font-normal"
                    severity="info"
                    value={lobHint(seg.payerName)}
                  />
                  {basis ? (
                    <span className="text-sm text-700">&middot; {basis}</span>
                  ) : null}
                </div>
                <div className="text-sm text-700 mt-2 ml-5">
                  {types.join(", ")}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex align-items-center justify-content-between mt-3">
        <small className="text-700">
          {skipped.length === 0
            ? "Modeling every line of business."
            : `Skipping ${skipped
                .map((s) => s.payerName ?? `Payer ${s.payerId}`)
                .join(", ")}. Their recommendations will be rejected.`}
        </small>
        <Button
          label={busy ? "Applying…" : "Apply selection"}
          icon="pi pi-check"
          disabled={busy}
          onClick={onApply}
        />
      </div>
    </div>
  );
}

export default LineOfBusinessPicker;
